// /memberships/:id — Membership Plan Detail (PAGES.md §3.94e)
//
// Hero with status-colored gradient + large preview card, 4 KPI tiles
// (active members, churned, revenue, avg lifetime), tabs for enrolled
// members and benefits breakdown. All actions gated at OWNER (50)+.

import { useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import {
  PageContainer,
  ErrorBoundary,
  Button,
  Skeleton,
  ConfirmDialog,
  Tabs,
  useToast,
} from '@bbpos/ui';
import type { ColumnDef } from '@bbpos/ui';
import type { CurrencyCode } from '@bbpos/types';
import { formatPaise } from '@/utils/currency';
import { useTenantCurrency } from '@/hooks/use-tenant-currency';
import { ApiError } from '@/lib/api-client';
import { useAuthStore } from '@/stores/use-auth-store';
import { ROLE_LEVELS } from '@/types/user';
import {
  useMembershipPlan,
  useMembershipPlanStats,
  useMembershipPlanMembers,
  useSetMembershipPlanActive,
  useDeleteMembershipPlan,
  useCreateMembershipPlan,
  MembershipTierPill,
  MembershipPreviewCard,
  formatDuration,
  formatTierLabel,
  listBenefits,
} from '@/features/memberships';
import type { EnrolledMemberRow } from '@/features/memberships';
import { ReportKpiRow, ReportTable, KpiIcons } from '@/features/reports';
import { formatShortDate, formatDateTime, customerInitials } from '@/features/customers';

type TabId = 'members' | 'benefits';

const TABS: { id: TabId; label: string }[] = [
  { id: 'members', label: 'Enrolled Members' },
  { id: 'benefits', label: 'Benefits' },
];

function makeMemberColumns(currency: CurrencyCode): ColumnDef<EnrolledMemberRow>[] {
  return [
  {
    key: 'customer_name',
    header: 'Member',
    render: (_v, row) => (
      <div className="flex items-center gap-3">
        <div className="flex h-9 w-9 shrink-0 items-center justify-center rounded-full bg-gradient-to-br from-amber-400 to-orange-500 text-xs font-bold text-white shadow-sm">
          {customerInitials(row.customer_name || 'Unknown')}
        </div>
        <div>
          <p className="text-sm font-semibold text-neutral-900 dark:text-neutral-100">
            {row.customer_name}
          </p>
          {row.customer_phone ? (
            <p className="mt-0.5 text-xs text-neutral-500 dark:text-neutral-400 tabular-nums">
              {row.customer_phone}
            </p>
          ) : null}
        </div>
      </div>
    ),
  },
  {
    key: 'enrolled_at',
    header: 'Enrolled',
    render: (_v, row) => (
      <span className="text-sm text-neutral-700 dark:text-neutral-200 tabular-nums">
        {formatDateTime(row.enrolled_at)}
      </span>
    ),
  },
  {
    key: 'expires_at',
    header: 'Expires',
    render: (_v, row) => {
      const expires = row.expires_at ? new Date(row.expires_at) : null;
      const daysLeft = expires ? Math.ceil((expires.getTime() - Date.now()) / 86_400_000) : null;
      const urgent = daysLeft != null && daysLeft >= 0 && daysLeft <= 14;
      const expired = daysLeft != null && daysLeft < 0;
      return (
        <div>
          <p className="text-sm text-neutral-700 dark:text-neutral-200 tabular-nums">
            {row.expires_at ? formatShortDate(row.expires_at) : '—'}
          </p>
          {expired ? (
            <span className="inline-flex items-center gap-1 rounded-full bg-neutral-100 dark:bg-neutral-800 px-2 py-0.5 text-[10px] font-bold uppercase tracking-wider text-neutral-500">
              Expired
            </span>
          ) : urgent ? (
            <span className="inline-flex items-center gap-1 rounded-full bg-warning-100 dark:bg-warning-900/40 px-2 py-0.5 text-[10px] font-bold uppercase tracking-wider text-warning-700 dark:text-warning-300 tabular-nums">
              {daysLeft === 0 ? 'Today' : `${daysLeft}d left`}
            </span>
          ) : null}
        </div>
      );
    },
  },
  {
    key: 'fee_paid',
    header: 'Fee Paid',
    numeric: true,
    render: (_v, row) => (
      <span className="text-sm font-semibold text-neutral-900 dark:text-neutral-100 tabular-nums">
        {formatPaise(row.fee_paid, currency)}
      </span>
    ),
  },
  {
    key: 'auto_renewal',
    header: 'Auto Renew',
    render: (_v, row) =>
      row.auto_renewal ? (
        <span className="inline-flex items-center gap-1 rounded-full bg-success-100 dark:bg-success-900/30 px-2 py-0.5 text-[10px] font-bold uppercase tracking-wider text-success-700 dark:text-success-300">
          On
        </span>
      ) : (
        <span className="inline-flex items-center gap-1 rounded-full bg-neutral-100 dark:bg-neutral-800 px-2 py-0.5 text-[10px] font-bold uppercase tracking-wider text-neutral-500">
          Off
        </span>
      ),
  },
  {
    key: 'status',
    header: 'Status',
    render: (_v, row) => {
      const tone =
        row.status === 'ACTIVE'
          ? 'bg-success-100 dark:bg-success-900/30 text-success-700 dark:text-success-300'
          : row.status === 'CANCELLED'
            ? 'bg-danger-100 dark:bg-danger-900/30 text-danger-700 dark:text-danger-300'
            : 'bg-neutral-100 dark:bg-neutral-800 text-neutral-500';
      return (
        <span className={`inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-[10px] font-bold uppercase tracking-wider ${tone}`}>
          {row.status}
        </span>
      );
    },
  },
  ];
}

function MembershipPlanDetailContent({ id }: { id: string }) {
  const navigate = useNavigate();
  const { toast } = useToast();
  const currency = useTenantCurrency();
  const memberColumns = useMemo(() => makeMemberColumns(currency), [currency]);
  const planQ = useMembershipPlan(id);
  const statsQ = useMembershipPlanStats(id);
  const [membersPage, setMembersPage] = useState(1);
  const membersQ = useMembershipPlanMembers(id, membersPage);
  const setActive = useSetMembershipPlanActive(id);
  const deleteMutation = useDeleteMembershipPlan(id);
  const duplicateMutation = useCreateMembershipPlan();

  const role = useAuthStore((s) => s.profile?.role ?? '');
  const roleLevel = ROLE_LEVELS[role] ?? 0;
  const canEdit = roleLevel >= ROLE_LEVELS.OWNER;
  const canOwn = roleLevel >= ROLE_LEVELS.OWNER;

  const [activeTab, setActiveTab] = useState<TabId>('members');
  const [confirmDelete, setConfirmDelete] = useState(false);
  const [confirmToggle, setConfirmToggle] = useState(false);

  // §3.94e: Redirect to /memberships with toast if plan not found (after loading)
  const hasRedirected = useRef(false);
  useEffect(() => {
    if (!planQ.isLoading && !planQ.data && !hasRedirected.current) {
      hasRedirected.current = true;
      toast({ variant: 'warning', title: 'Plan not found' });
      navigate('/memberships', { replace: true });
    }
  }, [planQ.isLoading, planQ.data, navigate, toast]);

  async function handleToggleActive() {
    if (!planQ.data) return;
    try {
      await setActive.mutateAsync(!planQ.data.is_active);
      toast({
        variant: 'success',
        title: planQ.data.is_active ? 'Plan deactivated' : 'Plan activated',
      });
      setConfirmToggle(false);
    } catch (err) {
      const msg = err instanceof ApiError ? err.message : 'Please try again.';
      toast({ variant: 'error', title: 'Could not update plan', description: msg });
    }
  }

  async function handleDelete() {
    try {
      await deleteMutation.mutateAsync();
      toast({ variant: 'success', title: 'Plan deleted' });
      navigate('/memberships');
    } catch (err) {
      const msg = err instanceof ApiError ? err.message : 'Please try again.';
      setConfirmDelete(false);
      // §3.94c: MEMBERSHIP_HAS_MEMBERS — active members prevent deletion
      if (err instanceof ApiError && /active members|has members|cannot delete/i.test(msg)) {
        toast({
          variant: 'warning',
          title: 'Cannot delete',
          description: msg,
        });
        return;
      }
      toast({ variant: 'error', title: 'Could not delete plan', description: msg });
    }
  }

  async function handleDuplicate() {
    if (!planQ.data) return;
    const src = planQ.data;
    try {
      const copy = await duplicateMutation.mutateAsync({
        name: `${src.name} (Copy)`,
        tier_level: src.tier_level,
        fee: src.fee,
        duration_days: src.duration_days,
        discount_percentage: src.discount_percentage,
        points_multiplier: src.points_multiplier,
        priority_service: src.priority_service,
        auto_renewal: src.auto_renewal,
        description: src.description,
        is_active: false, // duplicates start inactive — admin reviews then activates
      });
      toast({
        variant: 'success',
        title: 'Plan duplicated',
        description: `${copy.name} has been created (inactive).`,
      });
      navigate(`/memberships/${copy.id}`);
    } catch (err) {
      const msg = err instanceof ApiError ? err.message : 'Please try again.';
      toast({ variant: 'error', title: 'Could not duplicate plan', description: msg });
    }
  }

  const benefits = useMemo(
    () => (planQ.data ? listBenefits(planQ.data) : []),
    [planQ.data],
  );

  if (planQ.isLoading) {
    return (
      <>
        <Skeleton variant="rect" height={260} className="rounded-2xl mb-6" />
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 lg:gap-5 mb-6">
          {Array.from({ length: 4 }).map((_, i) => (
            <Skeleton key={i} variant="rect" height={120} className="rounded-xl" />
          ))}
        </div>
        <Skeleton variant="rect" height={360} className="rounded-2xl" />
      </>
    );
  }

  const plan = planQ.data;
  // Redirect handled by the useEffect above; render nothing while navigating away.
  if (!plan) return null;

  const heroGradient = plan.is_active
    ? 'from-amber-600 via-yellow-500 to-orange-600'
    : 'from-neutral-500 via-slate-500 to-neutral-600';
  const stats = statsQ.data;

  return (
    <div className="animate-fade-in">
      {/* Hero — split layout: left = metadata + actions, right = preview card */}
      <div
        className={`relative overflow-hidden rounded-2xl bg-gradient-to-br ${heroGradient} p-6 sm:p-8 text-white mb-6 shadow-[0_25px_70px_-25px_rgba(245,158,11,0.7)]`}
      >
        <div className="pointer-events-none absolute -top-24 -right-16 h-64 w-64 rounded-full bg-white/20 blur-3xl" />
        <div className="pointer-events-none absolute -bottom-20 -left-12 h-56 w-56 rounded-full bg-black/15 blur-3xl" />

        <div className="relative grid grid-cols-1 lg:grid-cols-5 gap-6 items-start">
          <div className="lg:col-span-3 min-w-0">
            <div className="flex items-center gap-2 flex-wrap">
              <MembershipTierPill tier={plan.tier_level} size="lg" className="ring-white/30" />
              <span className="inline-flex items-center gap-1.5 rounded-full bg-white/15 backdrop-blur-sm px-3 py-1 text-[10px] font-bold uppercase tracking-wider ring-1 ring-white/25">
                {plan.is_active ? 'Active' : 'Inactive'}
              </span>
            </div>
            <h1 className="mt-3 text-3xl sm:text-4xl font-bold tracking-tight drop-shadow-sm">
              {plan.name}
            </h1>
            <p className="mt-1 text-base sm:text-lg text-white/90 tabular-nums">
              <span className="font-bold">{formatPaise(plan.fee, currency)}</span>
              <span className="text-sm text-white/80"> / {formatDuration(plan.duration_days).long}</span>
            </p>
            {plan.description ? (
              <p className="mt-3 text-sm text-white/85 max-w-2xl leading-relaxed">
                {plan.description}
              </p>
            ) : null}

            <div className="mt-5 flex flex-wrap items-center gap-2">
              {canEdit ? (
                <Button
                  variant="secondary"
                  onClick={() => navigate(`/memberships/${id}/edit`)}
                  className="!bg-white/20 hover:!bg-white/30 !text-white !border-white/30 backdrop-blur-sm"
                >
                  Edit
                </Button>
              ) : null}
              {canEdit ? (
                <Button
                  variant="secondary"
                  onClick={handleDuplicate}
                  className="!bg-white/20 hover:!bg-white/30 !text-white !border-white/30 backdrop-blur-sm"
                  loading={duplicateMutation.isPending}
                >
                  Duplicate
                </Button>
              ) : null}
              {canOwn ? (
                <Button
                  variant="secondary"
                  onClick={() => setConfirmToggle(true)}
                  className="!bg-white/20 hover:!bg-white/30 !text-white !border-white/30 backdrop-blur-sm"
                >
                  {plan.is_active ? 'Deactivate' : 'Activate'}
                </Button>
              ) : null}
              {canOwn ? (
                <Button
                  variant="danger"
                  onClick={() => setConfirmDelete(true)}
                  className="!bg-white !text-danger-600 hover:!bg-white/90"
                >
                  Delete
                </Button>
              ) : null}
            </div>
          </div>

          {/* Preview card tucked into the hero on lg+ */}
          <div className="lg:col-span-2">
            <MembershipPreviewCard plan={plan} variant="live" interactive={false} />
          </div>
        </div>
      </div>

      {/* KPI row — §3.94e: active members, churned, revenue, avg lifetime */}
      <ReportKpiRow
        tiles={[
          {
            id: 'members',
            title: 'Active Members',
            value: (stats?.active_members ?? 0).toLocaleString('en-IN'),
            subLabel: 'Currently enrolled',
            tone: 'brand',
            icon: KpiIcons.users,
          },
          {
            id: 'churn',
            title: 'Churned (30d)',
            value: (stats?.cancelled_30d ?? 0).toLocaleString('en-IN'),
            subLabel:
              stats?.churn_rate_pct != null
                ? `${stats.churn_rate_pct.toFixed(1)}% churn rate`
                : 'No cancellations',
            tone:
              stats?.churn_rate_pct != null && stats.churn_rate_pct >= 10 ? 'warning' : 'violet',
            icon: KpiIcons.trendDown,
          },
          {
            id: 'revenue',
            title: 'Revenue',
            value: formatPaise(stats?.lifetime_revenue ?? 0, currency),
            subLabel: 'From this tier',
            tone: 'success',
            icon: KpiIcons.rupee,
          },
          {
            id: 'avg-lifetime',
            title: 'Avg Lifetime',
            value:
              stats?.avg_member_lifetime_days != null
                ? `${Math.round(stats.avg_member_lifetime_days)}d`
                : '—',
            subLabel: 'Average member duration',
            tone: 'indigo',
            icon: KpiIcons.receipt,
          },
        ]}
      />

      {/* Tabs */}
      <div className="mb-5">
        <Tabs
          items={TABS.map((t) => ({ id: t.id, label: t.label }))}
          activeId={activeTab}
          onChange={(tid) => setActiveTab(tid as TabId)}
          variant="pill"
        />
      </div>

      {activeTab === 'members' ? (
        <ReportTable<EnrolledMemberRow>
          title="Enrolled Members"
          subtitle="Customers currently on this plan plus recent cancellations."
          columns={memberColumns}
          data={membersQ.data?.data ?? []}
          loading={membersQ.isFetching}
          totalCount={membersQ.data?.meta.total}
          currentPage={membersPage}
          onPageChange={setMembersPage}
          emptyTitle="No enrolled members yet"
          emptyDescription="Once a customer signs up for this plan, they'll show up here."
        />
      ) : (
        <section className="rounded-2xl bg-white dark:bg-neutral-800 shadow-md p-6 sm:p-7 animate-slide-up">
          <h2 className="text-sm font-bold uppercase tracking-wider text-neutral-900 dark:text-neutral-100">
            {formatTierLabel(plan.tier_level)} Benefits
          </h2>
          {benefits.length === 0 ? (
            <p className="mt-4 text-sm text-neutral-500 dark:text-neutral-400">
              This plan has no benefits configured yet. Edit the plan to add a discount, points
              multiplier, priority service, or auto-renewal.
            </p>
          ) : (
            <ul className="mt-5 grid grid-cols-1 sm:grid-cols-2 gap-3">
              {benefits.map((b) => (
                <li
                  key={b.icon}
                  className="flex items-start gap-3 rounded-xl bg-gradient-to-br from-amber-50 to-yellow-50 dark:from-amber-900/20 dark:to-yellow-900/20 p-4 shadow-sm"
                >
                  <span className="flex h-9 w-9 shrink-0 items-center justify-center rounded-lg bg-gradient-to-br from-amber-500 to-orange-600 text-white shadow-sm">
                    <svg
                      className="h-5 w-5"
                      fill="none"
                      viewBox="0 0 24 24"
                      stroke="currentColor"
                      strokeWidth={2}
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        d="M5 13l4 4L19 7"
                      />
                    </svg>
                  </span>
                  <div>
                    <p className="text-sm font-semibold text-neutral-900 dark:text-neutral-100">
                      {b.label}
                    </p>
                  </div>
                </li>
              ))}
            </ul>
          )}
        </section>
      )}

      <ConfirmDialog
        open={confirmToggle}
        title={plan.is_active ? 'Deactivate plan?' : 'Activate plan?'}
        description={
          plan.is_active
            ? `This will stop new enrollments on ${plan.name}. Existing members keep their benefits until their term expires.`
            : `This will make ${plan.name} available for new enrollments again.`
        }
        confirmLabel={plan.is_active ? 'Deactivate' : 'Activate'}
        cancelLabel="Cancel"
        variant="warning"
        loading={setActive.isPending}
        onConfirm={handleToggleActive}
        onCancel={() => setConfirmToggle(false)}
      />

      <ConfirmDialog
        open={confirmDelete}
        title="Delete plan?"
        description={`Permanently delete ${plan.name}. Existing enrollments will remain on the customer records but the plan row itself will be gone. This cannot be undone.`}
        confirmLabel="Delete permanently"
        cancelLabel="Cancel"
        variant="danger"
        loading={deleteMutation.isPending}
        onConfirm={handleDelete}
        onCancel={() => setConfirmDelete(false)}
      />
    </div>
  );
}

export function MembershipPlanDetailPage() {
  const { id } = useParams<{ id: string }>();
  const planQ = useMembershipPlan(id);
  const planName = planQ.data?.name;

  if (!id) {
    return (
      <PageContainer
        title="Membership Plan"
        breadcrumbs={[{ label: 'Memberships', href: '/memberships' }]}
      >
        <div className="p-6 text-neutral-500">Missing plan id.</div>
      </PageContainer>
    );
  }
  return (
    <PageContainer
      title={planName ?? 'Membership Plan'}
      breadcrumbs={[
        { label: 'Home', href: '/home' },
        { label: 'Memberships', href: '/memberships' },
        { label: planName ?? 'Plan' },
      ]}
    >
      <ErrorBoundary resetKey={`membership-${id}`}>
        <MembershipPlanDetailContent id={id} />
      </ErrorBoundary>
    </PageContainer>
  );
}
